<template>
    <div
        class="filedrop"
        :class="{
            'filedrop--vertical': vertical,
            'filedrop--dragging': dragging,
            'filedrop--invalid': invalid,
            'filedrop--disabled': disabled
        }"
    >
        <div
            class="filedrop__zone"
            :style="minHeight ? `min-height: ${minHeight}` : null"
            role="button"
            @drop.prevent="handleDrop"
            @dragover.prevent="handleDragover"
            @dragenter.prevent="handleDragenter"
            @dragleave.prevent="handleDragleave"
            @click="handleClick"
            @keydown="handleClick"
        >
            <template v-if="findSlot('waiting') && !dragging">
                <slot name="waiting" />
            </template>

            <template v-else>
                <v-layout align-center :column="vertical">
                    <v-icon class="filedrop__zone__icon" :color="zoneIconColour">
                        {{ zoneIcon }}
                    </v-icon>
                    <div class="filedrop__zone__text text--secondary">
                        <p class="filedrop__zone__label mb-0">{{ zoneLabel }}</p>
                        <p class="filedrop__zone__sublabel mb-0 mt-n1">{{ zoneSublabel }}</p>
                    </div>
                    <v-spacer />
                    <app-button
                        v-if="!dragging || vertical"
                        class="filedrop__zone__browse"
                        :disabled="disabled || sending"
                        secondary
                        :text="$t('browse')"
                    />
                </v-layout>

                <v-progress-linear v-if="sending" indeterminate color="primaryBackground" :height="2" />
            </template>
        </div>

        <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
        <input
            ref="fileInput"
            type="file"
            :multiple="max > 1"
            :accept="accept"
            style="display: none"
            @change="handleFileInput"
        />
    </div>
</template>

<script>
import request from '@/api/lib/request';
import { attachHeaders } from '@/api/lib/request';

const mimetypeWhiteList = [
    'application/pdf',
    'application/msword',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.presentation',
    'application/vnd.ms-excel',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/svg+xml',
    'image/webp',
    'video/mp4',
    'video/mpeg',
    'video/ogg',
    'video/mp2t',
    'video/webm',
    'video/3gpp',
    'video/3gpp2',
    'text/csv'
];

export default {
    name: 'AppFileDrop',
    props: {
        value: {
            type: Array,
            default: null
        },
        label: {
            type: String,
            default: null
        },
        max: {
            type: Number,
            default: 1
        },
        accept: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        sendOnDrop: {
            type: Boolean,
            default: false
        },
        sendFileUrl: {
            type: String,
            default: null
        },
        sendFileMethod: {
            type: String,
            default: 'PUT'
        },
        vertical: {
            type: Boolean,
            default: false
        },
        minHeight: {
            type: String,
            default: null
        }
    },
    data: () => ({
        dragging: false,
        invalid: false,
        sending: false
    }),
    computed: {
        files: {
            get() {
                return this.value || [];
            },
            set(v) {
                this.$emit('input', v);
            }
        },
        hasMaxFiles() {
            return this.files.length >= this.max;
        },
        zoneIcon() {
            if (this.invalid) {
                return this.getIcon('error', 'fad');
            }

            return this.getIcon('fileImage', 'fad');
        },
        zoneIconColour() {
            if (this.invalid) {
                return 'error';
            }

            if (this.dragging) {
                return 'primaryText';
            }

            return 'grey';
        },
        zoneLabel() {
            if (this.invalid === true) {
                return this.$t('file_type_not_allowed');
            } else if (this.invalid) {
                return this.invalid;
            }

            if (this.label) {
                return this.label;
            }

            if (this.max > 1) {
                return this.$t('drop_files_here_to_upload');
            } else {
                return this.$t('drop_file_here_to_upload');
            }
        },
        zoneSublabel() {
            if (!this.accept) {
                return null;
            }

            const accepts = this.accept.split(',').map(ext => ext.toLowerCase());

            let text = this.$t('accepts');
            text += ` ${accepts.join(', ')}`;
            text += ` ${this.$t('files').toLowerCase()}`;

            const lastComma = text.lastIndexOf(',');

            if (lastComma > -1) {
                text = text.substring(0, lastComma) + ` ${this.$t('and')}` + text.substring(lastComma + 1);
            }

            return text;
        }
    },
    methods: {
        addFiles(files) {
            for (const file of files) {
                if (this.isValidFileType(file)) {
                    const files = this.files;
                    files.push(file);
                    files.splice(0, files.length - this.max);
                    this.files = files;
                } else {
                    this.invalid = this.$t('file_type_not_allowed');
                }
            }
        },
        removeFile(index) {
            const files = this.files;
            files.splice(index, 1);
            this.files = files;
        },
        isValidFileType(files) {
            const acceptedTypes = this.accept.split(',').map(ext => ext.toLowerCase());

            if (!(files instanceof FileList) && !Array.isArray(files)) {
                files = [files];
            }

            for (const file of files) {
                if (!mimetypeWhiteList.includes(file.type)) {
                    return false;
                }

                const extension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
                if (this.accept && !acceptedTypes.includes(file.type) && !acceptedTypes.includes(extension)) {
                    return false;
                }
            }

            return true;
        },
        //
        handleClick(e) {
            e.preventDefault();

            if (this.disabled || this.sending) {
                return;
            }

            this.invalid = false;
            this.$refs.fileInput.click();
        },
        handleFileInput(e) {
            e.preventDefault();

            if (this.disabled || this.sending) {
                return;
            }

            this.addFiles(e.target.files);

            if (this.sendOnDrop) {
                this.send();
            }
        },
        handleDrop(e) {
            e.preventDefault();

            if (this.disabled || this.sending) {
                return;
            }

            this.dragging = false;
            this.invalid = false;
            this.addFiles(e.dataTransfer.files);

            if (this.sendOnDrop) {
                this.send();
            }
        },
        handleDragover(e) {
            e.preventDefault();

            if (this.disabled || this.sending) {
                return;
            }

            this.dragging = true;
            this.invalid = false;
        },
        handleDragenter(e) {
            e.preventDefault();

            if (this.disabled || this.sending) {
                return;
            }

            this.dragging = true;
            this.invalid = false;
        },
        handleDragleave(e) {
            e.preventDefault();

            if (this.disabled || this.sending) {
                return;
            }

            this.dragging = false;
        },
        //
        async send() {
            const file = this.files && this.files.length ? this.files[0] : null;

            if (!file || !this.sendFileUrl) {
                return;
            }

            this.sending = true;
            this.$emit('sending');

            try {
                const response = await request({
                    method: this.sendFileMethod || 'PUT',
                    url: this.sendFileUrl,
                    data: { file },
                    headers: attachHeaders({ 'Content-Type': 'multipart/form-data' })
                });

                this.$emit('success', file, response);

                this.files = [];
            } catch (error) {
                this.invalid = error.message;
                this.$emit('error', file, error);
            }

            this.sending = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.filedrop {
    &__zone {
        border: 2px dashed var(--v-background-darken4);
        background: var(--v-background-base);
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s ease-in-out;
        &__icon {
            padding: 1rem 1.2rem;
        }
        &__text {
            padding: 0.5rem 0;
        }
        &__label {
            font-weight: bold;
            transition: all 0.15s ease-in-out;
        }
        &__sublabel {
            font-size: 0.875rem;
            transition: all 0.15s ease-in-out;
        }
        &__browse {
            margin: 0 1rem;
        }
    }

    &--dragging {
        .filedrop__zone {
            border: 2px dashed var(--v-primaryBackground-base);
            background: var(--v-primaryLight-base);
        }
        .filedrop__zone__text p {
            color: var(--v-primaryText-base);
        }
    }

    &--invalid {
        .filedrop__zone {
            border: 2px dashed var(--v-error-base);
            background: var(--v-error-lighten5);
        }
        .filedrop__zone__text p {
            color: var(--v-error-darken2);
        }
    }

    &--disabled {
        .filedrop__zone {
            cursor: no-drop;
            border: 2px dashed var(--v-background-darken2);
            background: var(--v-background-darken2);
        }
        .filedrop__zone__text {
            opacity: 0.7;
        }
    }

    &--vertical {
        .filedrop__zone {
            &__icon {
                margin-top: 1rem;
                padding: 1.2rem 1rem 0.5rem 1rem;
            }
            &__text {
                padding: 0 1rem;
            }
            &__browse {
                margin: 1rem 0 0.5rem;
            }
        }
    }
}
</style>
